/**
 * The box you write a support message in. One of them, everywhere.
 *
 * There were three: the new-report composer, the reply in the bubble, and the
 * reply on the staff thread. They had drifted into three different widgets —
 * different borders, different places for the pictures, different ways to add
 * one, and Ctrl+V working in some of them and not others. Nobody can learn a
 * widget that changes shape depending on which support box they are looking
 * at, so this is the only one.
 *
 * What never varies: the whole thing is a single bordered object, pictures
 * waiting to be sent live INSIDE that border above the text, a paperclip
 * offers the capture sources, and pasting an image attaches it.
 *
 * What varies is `layout`, and only because the field's height forces it. A
 * one-line box takes its controls beside it, the way WhatsApp and iMessage
 * do. A box that starts multi-line and grows takes a toolbar underneath, the
 * way Slack and Teams do — controls beside a growing field either stretch
 * into tall columns or drift away from its baseline.
 */

import SendIcon from "@mui/icons-material/Send";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import InputBase from "@mui/material/InputBase";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { imageFromPaste } from "./support-capture-engines";
import type { DraftPicture } from "./support-draft-pictures";
import {
	type ReplyPictureSources,
	SupportAttachButton,
	SupportReplyPictures,
} from "./support-reply-pictures";

interface SupportComposerProps {
	value: string;
	onChange: (value: string) => void;
	placeholder: string;
	onSend: () => void;
	isSending: boolean;

	pictures: DraftPicture[];
	/** A picture arriving from the paperclip or from a paste. */
	onAddPicture: (blob: Blob) => void;
	onEditPicture: (key: number) => void;
	onRemovePicture: (key: number) => void;
	sources: ReplyPictureSources;

	/** Follows the field's height, never taste. See the module docstring. */
	layout: "inline" | "stacked";
	/** Only meaningful when stacked; an inline field is one line by definition. */
	minRows?: number;
	/** The bubble is a column ~360px wide, so its thumbnails are smaller. */
	compact?: boolean;
	sendLabel?: string;
	/** Distinguishes the three in tests and in the DOM. */
	dataComponent: string;
	autoFocus?: boolean;
}

const SupportComposer: React.FC<SupportComposerProps> = ({
	value,
	onChange,
	placeholder,
	onSend,
	isSending,
	pictures,
	onAddPicture,
	onEditPicture,
	onRemovePicture,
	sources,
	layout,
	minRows = 2,
	compact = false,
	sendLabel = "Send",
	dataComponent,
	autoFocus = false,
}) => {
	const attach = <SupportAttachButton onAdd={onAddPicture} sources={sources} />;
	const send = (
		<Button
			variant="contained"
			size="small"
			startIcon={<SendIcon />}
			disabled={isSending || !value.trim()}
			onClick={onSend}
			data-component={`${dataComponent}-send`}
		>
			{sendLabel}
		</Button>
	);
	const input = (
		<InputBase
			fullWidth
			multiline
			autoFocus={autoFocus}
			minRows={layout === "inline" ? 1 : minRows}
			maxRows={layout === "inline" ? 6 : undefined}
			placeholder={placeholder}
			value={value}
			onChange={(event) => onChange(event.target.value)}
			onPaste={(event) => {
				// Ctrl+V after a snipping tool, which is how most people take a
				// screenshot at all. Swallowed ONLY when the clipboard really
				// held an image — a text paste must stay a text paste.
				const pasted = imageFromPaste(event.clipboardData);
				if (!pasted) return;
				event.preventDefault();
				onAddPicture(pasted);
			}}
			sx={
				layout === "inline"
					? { fontSize: compact ? 14 : undefined }
					: { px: 1.5, py: 1.25, fontSize: compact ? 14 : undefined }
			}
			data-component={`${dataComponent}-input`}
		/>
	);

	return (
		<Paper
			variant="outlined"
			sx={{
				borderRadius: 1,
				// The container takes the focus ring the input gave up, so
				// clicking anywhere in it lights the whole message, not an
				// inner box inside an outer one.
				"&:focus-within": {
					borderColor: "primary.main",
					boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
				},
			}}
			data-component={dataComponent}
		>
			{/* Inside the border, above the text: a picture waiting to be sent
			    is part of the message being written. Renders nothing at all
			    until there is one, so an empty box keeps its height. */}
			<Box
				sx={{
					px: compact ? 1 : 1.5,
					pt: pictures.length ? (compact ? 1 : 1.5) : 0,
				}}
			>
				<SupportReplyPictures
					compact={compact}
					pictures={pictures}
					onEdit={onEditPicture}
					onRemove={onRemovePicture}
				/>
			</Box>

			{layout === "inline" ? (
				<Stack
					direction="row"
					spacing={0.5}
					alignItems="center"
					sx={{ p: 0.5 }}
				>
					{attach}
					{input}
					{send}
				</Stack>
			) : (
				<>
					{input}
					{/* px matches the input's, so the send button's right edge and
					    the text's right edge are the same line. At px 1 against
					    the input's 1.5 the controls hung four pixels further out
					    than the text they belong to, and nothing aligned. */}
					<Stack
						direction="row"
						alignItems="center"
						justifyContent="space-between"
						sx={{ px: 1.5, pb: 1 }}
					>
						{attach}
						{send}
					</Stack>
				</>
			)}
		</Paper>
	);
};

export default SupportComposer;
